import dayjs from "dayjs";

/** Item do histórico de remissões de um DocumentReference. */
export type DocumentReissueHistoryItem = {
  id?: string;
  date: string;
  dateRaw?: string;
  status?: string;
  binaryUrl?: string;
  isLatest: boolean;
  isFirst: boolean;
};

export type DocumentChainDates = {
  /** Data da primeira emissão (rótulo amigável DD/MM/YYYY). */
  firstDate: string;
  /** Data da Última reemissão / versão mais recente. */
  lastDate: string;
  /** Quantidade de remissões após a emissão original (history.length - 1). */
  reissueCount: number;
  /** True se o documento foi remissão (relatesTo ou cadeia > 1). */
  hasReissueHistory: boolean;
  history: DocumentReissueHistoryItem[];
};

type EncounterDateMap = Map<string, string>;

/** Monta mapa Encounter.id → data bruta (ISO) a partir das entries do summary. */
export function buildEncounterDateMap(
  encounterEntries: any[] | undefined,
): EncounterDateMap {
  const map: EncounterDateMap = new Map();
  encounterEntries?.forEach((el: any) => {
    const enc = el?.resource ?? el?.encounter ?? el;
    if (!enc?.id) return;
    const raw =
      enc?.actualPeriod?.start ||
      enc?.period?.start ||
      enc?.meta?.lastUpdated;
    if (raw) map.set(String(enc.id), raw);
  });
  return map;
}

function getContextList(resource: any): any[] {
  if (Array.isArray(resource?.context)) return resource.context;
  if (resource?.context) return [resource.context];
  return [];
}

/** Extrai o id do Encounter referenciado no context do DocumentReference. */
export function getDocumentEncounterId(resource: any): string | undefined {
  const fromContextRef = getContextList(resource).find((ctx: any) =>
    typeof ctx?.reference === "string"
      ? ctx.reference.includes("Encounter")
      : false,
  )?.reference?.split("/")?.[1];

  if (fromContextRef) return fromContextRef;

  // FHIR R4 clássico: context.encounter[].reference
  const encounters = resource?.context?.encounter;
  const list = Array.isArray(encounters)
    ? encounters
    : encounters
      ? [encounters]
      : [];
  return list
    .find((e: any) => e?.reference?.includes("Encounter"))
    ?.reference?.split("/")?.[1];
}

function formatDate(raw: string | undefined): string {
  if (!raw) return "-";
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : "-";
}

function getDocDateRaw(resource: any): string | undefined {
  return resource?.date || resource?.period?.start || resource?.meta?.lastUpdated;
}

function getBasedOnList(resource: any): any[] {
  if (Array.isArray(resource?.basedOn)) return resource.basedOn;
  if (resource?.basedOn) return [resource.basedOn];
  return [];
}

/**
 * Chave estável das referências basedOn — remissões reutilizam os mesmos
 * MedicationRequest/ServiceRequest, então dá para agrupar mesmo sem relatesTo.
 */
function getBasedOnKey(resource: any): string | undefined {
  const refs = getBasedOnList(resource)
    .map((b: any) => b?.reference)
    .filter((ref: unknown): ref is string => typeof ref === "string" && !!ref)
    .map((ref) => ref.trim())
    .sort();
  if (!refs.length) return undefined;
  return `basedOn:${refs.join("|")}`;
}

/** Chaves de indexação para amarrar relatesTo → documento alvo. */
function getDocumentKeys(resource: any): string[] {
  const keys: string[] = [];
  if (resource?.id) keys.push(`id:${resource.id}`);

  const identifiers = Array.isArray(resource?.identifier)
    ? resource.identifier
    : resource?.identifier
      ? [resource.identifier]
      : [];

  identifiers.forEach((id: any) => {
    if (id?.system && id?.value) {
      keys.push(`identifier:${id.system}|${id.value}`);
      keys.push(
        `identifier:${encodeURIComponent(id.system)}%7C${encodeURIComponent(id.value)}`,
      );
      keys.push(`identifier:${id.system}%7C${id.value}`);
    }
    if (id?.value) {
      keys.push(`value:${id.value}`);
      // uuid puro sem prefixo urn:uuid:
      if (String(id.value).startsWith("urn:uuid:")) {
        keys.push(`value:${String(id.value).replace(/^urn:uuid:/, "")}`);
      }
    }
  });

  return keys;
}

function getRelatesToList(resource: any): any[] {
  if (Array.isArray(resource?.relatesTo)) return resource.relatesTo;
  if (resource?.relatesTo) return [resource.relatesTo];
  return [];
}

function isReplacesCode(code: unknown): boolean {
  if (code === "replaces") return true;
  if (typeof code === "object" && code !== null) {
    const c = code as any;
    return (
      c?.coding?.[0]?.code === "replaces" ||
      c?.code === "replaces" ||
      c?.text === "replaces"
    );
  }
  return false;
}

/** Indica se o DocumentReference é uma remissão (substitui outro). */
export function documentHasReplaces(resource: any): boolean {
  return getRelatesToList(resource).some((r) => isReplacesCode(r?.code));
}

/** Resolve a chave do documento substituído (relatesTo code=replaces). */
export function getReplacesTargetKey(resource: any): string | undefined {
  const replaces = getRelatesToList(resource).find((r) =>
    isReplacesCode(r?.code),
  );
  if (!replaces) return undefined;

  // target.reference (query ou DocumentReference/id)
  const ref =
    typeof replaces?.target?.reference === "string"
      ? replaces.target.reference
      : typeof replaces?.target === "string"
        ? replaces.target
        : undefined;

  if (ref) {
    if (ref.includes("DocumentReference/") && !ref.includes("?")) {
      const id = ref.split("/").pop()?.split("?")[0];
      return id ? `id:${id}` : undefined;
    }

    const match = ref.match(/identifier=([^&]+)/i);
    if (match?.[1]) {
      const decoded = decodeURIComponent(match[1]);
      return `identifier:${decoded}`;
    }
  }

  // target.identifier (system + value) — formato comum após XML→JSON
  const targetId = replaces?.target?.identifier;
  if (targetId?.system && targetId?.value) {
    return `identifier:${targetId.system}|${targetId.value}`;
  }
  if (targetId?.value) {
    return `value:${targetId.value}`;
  }

  return undefined;
}

function resolveTargetIndex(
  targetKey: string,
  keyToIndex: Map<string, number>,
): number | undefined {
  if (keyToIndex.has(targetKey)) return keyToIndex.get(targetKey);

  if (targetKey.startsWith("identifier:")) {
    const raw = targetKey.slice("identifier:".length);
    const value = raw.includes("|")
      ? raw.split("|").pop()
      : raw.includes("%7C")
        ? decodeURIComponent(raw.split("%7C").pop() || "")
        : raw;
    if (value && keyToIndex.has(`value:${value}`)) {
      return keyToIndex.get(`value:${value}`);
    }
    if (value?.startsWith("urn:uuid:")) {
      const bare = value.replace(/^urn:uuid:/, "");
      if (keyToIndex.has(`value:${bare}`)) return keyToIndex.get(`value:${bare}`);
    }
    for (const [key, idx] of keyToIndex.entries()) {
      if (key.endsWith(value || "") || key.includes(`|${value}`)) return idx;
    }
  }

  if (targetKey.startsWith("value:")) {
    const value = targetKey.slice("value:".length);
    if (keyToIndex.has(`value:${value}`)) return keyToIndex.get(`value:${value}`);
    if (value.startsWith("urn:uuid:")) {
      const bare = value.replace(/^urn:uuid:/, "");
      if (keyToIndex.has(`value:${bare}`)) return keyToIndex.get(`value:${bare}`);
    }
  }

  return undefined;
}

/**
 * Agrupa DocumentReference entries ligados por relatesTo/replaces
 * e/ou pelo mesmo conjunto de basedOn (remissões reutilizam os MRs/SRs).
 */
export function groupDocumentReferenceChains(entries: any[]): any[][] {
  if (!entries?.length) return [];

  const parent = new Map<number, number>();
  const find = (i: number): number => {
    const p = parent.get(i) ?? i;
    if (p !== i) {
      const root = find(p);
      parent.set(i, root);
      return root;
    }
    return i;
  };
  const union = (a: number, b: number) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent.set(ra, rb);
  };

  entries.forEach((_, i) => parent.set(i, i));

  const keyToIndex = new Map<string, number>();
  entries.forEach((entry, i) => {
    getDocumentKeys(entry?.resource).forEach((k) => {
      if (!keyToIndex.has(k)) keyToIndex.set(k, i);
    });
  });

  // 1) Cadeia explícita relatesTo → replaces
  entries.forEach((entry, i) => {
    const targetKey = getReplacesTargetKey(entry?.resource);
    if (!targetKey) return;
    const targetIdx = resolveTargetIndex(targetKey, keyToIndex);
    if (targetIdx !== undefined) union(i, targetIdx);
  });

  // 2) Fallback: mesmo basedOn = mesma linha lógica de remissões
  const basedOnToIndex = new Map<string, number>();
  entries.forEach((entry, i) => {
    const basedOnKey = getBasedOnKey(entry?.resource);
    if (!basedOnKey) return;
    const existing = basedOnToIndex.get(basedOnKey);
    if (existing !== undefined) {
      union(i, existing);
    } else {
      basedOnToIndex.set(basedOnKey, i);
    }
  });

  const groups = new Map<number, any[]>();
  entries.forEach((entry, i) => {
    const root = find(i);
    if (!groups.has(root)) groups.set(root, []);
    groups.get(root)!.push(entry);
  });

  return Array.from(groups.values());
}

/** Escolhe a versão a exibir na lista: status=current, senão a mais recente. */
export function pickLatestDocumentEntry(chain: any[]): any {
  if (!chain?.length) return undefined;

  const currents = chain.filter((e) => e?.resource?.status === "current");
  const pool = currents.length ? currents : chain;

  return [...pool].sort((a, b) => {
    const da = dayjs(getDocDateRaw(a?.resource) || 0).valueOf();
    const db = dayjs(getDocDateRaw(b?.resource) || 0).valueOf();
    return db - da;
  })[0];
}

/** Ordena a cadeia da mais antiga para a mais recente. */
function sortChainOldestFirst(chain: any[]): any[] {
  return [...chain].sort((a, b) => {
    const da = dayjs(getDocDateRaw(a?.resource) || 0).valueOf();
    const db = dayjs(getDocDateRaw(b?.resource) || 0).valueOf();
    return da - db;
  });
}

/**
 * Calcula primeira emissão, Última reemissão e histórico da cadeia.
 * Preferência da primeira data: Encounter → period.start (preservado na remissão) → date.
 *
 * Mesmo com só a versão current no bundle (XDS), detecta remissão via relatesTo
 * e usa period.start vs date para primeira/última.
 */
export function resolveChainDates(
  chain: any[],
  encounterDateMap?: EncounterDateMap,
): DocumentChainDates {
  const ordered = sortChainOldestFirst(chain);
  const firstEntry = ordered[0];
  const lastEntry = ordered[ordered.length - 1];
  const latestResource =
    pickLatestDocumentEntry(chain)?.resource ?? lastEntry?.resource;

  const hasRelatesTo = documentHasReplaces(latestResource);
  const chainLength = ordered.length;

  const firstEncounterId = getDocumentEncounterId(firstEntry?.resource);
  const firstFromEncounter =
    firstEncounterId && encounterDateMap?.get(firstEncounterId);

  // period.start é copiado na remissão — boa proxy da emissão original
  const periodStarts = ordered
    .map((e) => e?.resource?.period?.start)
    .filter(Boolean) as string[];
  const oldestPeriod = periodStarts.length
    ? [...periodStarts].sort(
        (a, b) => dayjs(a).valueOf() - dayjs(b).valueOf(),
      )[0]
    : undefined;

  const firstRaw =
    firstFromEncounter ||
    oldestPeriod ||
    firstEntry?.resource?.period?.start ||
    getDocDateRaw(firstEntry?.resource);

  const lastRaw = getDocDateRaw(latestResource);

  const firstDate = formatDate(firstRaw);
  const lastDate = formatDate(lastRaw);

  // period.start ≠ date → forte indício de remissão (period preservado, date atualizado)
  const periodVsDateDiffer =
    !!oldestPeriod &&
    !!lastRaw &&
    formatDate(oldestPeriod) !== formatDate(lastRaw);

  const history: DocumentReissueHistoryItem[] = ordered.map((entry, index) => {
    const resource = entry?.resource;
    const raw = getDocDateRaw(resource);
    return {
      id: resource?.id,
      date: formatDate(raw),
      dateRaw: raw,
      status: resource?.status,
      binaryUrl: resource?.content?.[0]?.attachment?.url || "",
      isFirst: index === 0,
      isLatest: index === ordered.length - 1,
    };
  });

  // Só a versão atual no bundle: sintetiza original + atual quando há sinal de remissão
  const shouldSynthesizeHistory =
    chainLength === 1 &&
    firstDate !== lastDate &&
    firstDate !== "-" &&
    lastDate !== "-" &&
    (hasRelatesTo || periodVsDateDiffer);

  if (shouldSynthesizeHistory) {
    history.unshift({
      id: undefined,
      date: firstDate,
      dateRaw: firstRaw,
      status: "superseded",
      binaryUrl: "",
      isFirst: true,
      isLatest: false,
    });
    if (history[1]) {
      history[1] = { ...history[1], isFirst: false, isLatest: true };
    }
  }

  // Remissão real: cadeia > 1, period≠date, ou relatesTo com datas distintas
  const hasReissueHistory =
    chainLength > 1 ||
    history.length > 1 ||
    periodVsDateDiffer ||
    (hasRelatesTo &&
      firstDate !== lastDate &&
      firstDate !== "-" &&
      lastDate !== "-");

  const reissueCount = hasReissueHistory
    ? Math.max(1, history.length - 1, chainLength - 1)
    : 0;

  return {
    firstDate,
    lastDate,
    reissueCount,
    hasReissueHistory,
    history,
  };
}
